import {OrderViewStore} from "../view/OrderViewStore";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {OrderViewModels} from "../../models/OrderViewModel";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {OrderEvent} from "../../models/events/OrderEvent";
import {OrderEventType} from "../../models/events/OrderEventType";
import {ClientEventService} from "../../../services/client-events/ClientEventService";
import {ClientEventType} from "../../../services/client-events/models/ClientEventType";
import {isIressOrderClientEvent, isOrderClientEvent} from "../../../services/client-events/models/ClientEventRecord";
import {IosServerName} from "../../models/OrderRecord";
import {isSimulatedOrderModel, SimulatedOrderViewModel} from "app/orders/models/simulated/SimulatedOrderViewModel";
import {IressOrderViewModel, isIressOrderModel} from "../../models/iress/IressOrderViewModel";
import {FeatureNames} from "../../../types/UserConfig";
import {CurrentUserService} from "../../../services/user/CurrentUserService";

interface IressOrderDefinition {
    readonly orderNumber: number;
    readonly serverName: IosServerName;
}

export class OrderEventStore {

    public static id = "orderEventStore";

    private readonly events$ = new ReplaySubject<OrderEvent>(1);

    private readonly orderCreated$ = new ReplaySubject<number>(1);
    private readonly iressOrderCreated$ = new ReplaySubject<IressOrderDefinition>(1);
    private readonly orderCompleted$ = new ReplaySubject<number>(1);
    private readonly iressOrderCompleted$ = new ReplaySubject<IressOrderDefinition>(1);

    /** @ngInject */
    public constructor(private readonly orderViewStore: OrderViewStore,
                       private readonly clientEventService: ClientEventService,
                       private readonly currentUserService: CurrentUserService) {
        if (currentUserService.hasFeature(FeatureNames.SCREEN_EQUITY_ORDERS, FeatureNames.SCREEN_OPTION_ORDERS)) {
            this.init();
        }
    }

    private init() {
        this.watchOrderCreated();
        this.watchOrderCompleted();
        this.watchScopeEvents();
    }

    private watchScopeEvents() {
        this.clientEventService
            .events()
            .filter(isOrderClientEvent)
            .filter((evt) => evt.type === ClientEventType.ORDER_CREATED)
            .subscribe((evt) => {
                if (isIressOrderClientEvent(evt)) {
                    const def = {orderNumber: evt.orderNumber, serverName: evt.serverName};
                    this.iressOrderCreated$.next(def);
                } else {
                    this.orderCreated$.next(evt.orderNumber);
                }
            });

        this.clientEventService
            .events()
            .filter(isOrderClientEvent)
            .filter((evt) => evt.type === ClientEventType.ORDER_COMPLETED)
            .subscribe((evt) => {
                if (isIressOrderClientEvent(evt)) {
                    const def = {orderNumber: evt.orderNumber, serverName: evt.serverName};
                    this.iressOrderCompleted$.next(def);
                } else {
                    this.orderCompleted$.next(evt.orderNumber);
                }
            });
    }

    private watchOrderCreated() {
        this.orderCreated$
            .observeOn(Scheduler.async)
            .subscribe((orderId) => this.awaitCreatedOrder(orderId));
        this.iressOrderCreated$
            .observeOn(Scheduler.async)
            .subscribe((def) => this.awaitCreatedIressOrder(def));
    }

    private watchOrderCompleted() {
        this.orderCompleted$
            .observeOn(Scheduler.async)
            .subscribe((orderId) => this.awaitCompletedOrder(orderId));
        this.iressOrderCompleted$
            .observeOn(Scheduler.async)
            .subscribe((def) => this.awaitCompletedIressOrder(def));
    }

    /* Every time we get a created order, we need to wait for the model to appear and then return it */
    private awaitCreatedOrder(orderId: number) {
        this.orderViewStore
            .orders
            .observeOn(Scheduler.async)
            .map((models) => this.findModel(orderId, models))
            .filter(isNotNull)
            .take(1)
            .map((model) => OrderEvent.created(model))
            .subscribe((event) => this.events$.next(event));
    }

    private awaitCreatedIressOrder(def: IressOrderDefinition) {
        this.orderViewStore
            .orders
            .observeOn(Scheduler.async)
            .map((models) => this.findIressModel(def, models))
            .filter(isNotNull)
            .take(1)
            .map((model) => OrderEvent.created(model))
            .subscribe((event) => this.events$.next(event));
    }

    private findModel(orderId: number, models: OrderViewModels): SimulatedOrderViewModel | undefined {
        return models.filter(isSimulatedOrderModel)
                     .find((m) => m.id === orderId);
    }

    private findIressModel(def: IressOrderDefinition, models: OrderViewModels): IressOrderViewModel | undefined {
        return models.filter(isIressOrderModel)
                     .find((m) => (m.orderNumber === def.orderNumber) && (m.serverName === def.serverName));
    }

    private awaitCompletedOrder(orderId: number) {
        this.orderViewStore
            .orders
            .map((models) => this.findCompletedModel(orderId, models))
            .filter(isNotNull)
            .take(1)
            .map((model) => OrderEvent.completed(model))
            .subscribe((event) => this.events$.next(event));
    }

    private awaitCompletedIressOrder(def: IressOrderDefinition) {
        this.orderViewStore
            .orders
            .map((models) => this.findCompletedIressModel(def, models))
            .filter(isNotNull)
            .take(1)
            .map((model) => OrderEvent.completed(model))
            .subscribe((event) => this.events$.next(event));
    }

    private findCompletedModel(orderId: number, models: OrderViewModels): SimulatedOrderViewModel | undefined {
        return models.filter((m) => m.isCompleted)
                     .filter(isSimulatedOrderModel)
                     .find((m) => m.id === orderId);
    }

    private findCompletedIressModel(def: IressOrderDefinition,
                                    models: OrderViewModels): IressOrderViewModel | undefined {
        return models.filter((m) => m.isCompleted)
                     .filter(isIressOrderModel)
                     .find((m) => (m.serverName === def.serverName) && (m.orderNumber === def.orderNumber));
    }

    public events(...eventTypes: Array<OrderEventType>): Observable<OrderEvent> {
        if (eventTypes.length === 0) {
            return this.events$.observeOn(Scheduler.async);
        } else {
            return this.events$
                       .observeOn(Scheduler.async)
                       .filter((event) => eventTypes.includes(event.type));
        }
    }
}
